/**
 * SalesAllocationMassUploadDialog — bulk insert MANY Sales Allocation rows in
 * one shot. Converted from Access `frmCtrlSalesQrySaleAlcAddMany` ("Mass Upload"
 * / `btnInsert` on the Sales Allocation tab).
 *
 * Access shape: a header Client combo (`cp_vn`) + a subform datasheet bound to
 * the temp table `public_tmp_addmany_salesalc`; `btnSave` scans every temp row,
 * validates, then INSERTs each into `public_sales_allocation_scs`. There is no
 * "row selected" precondition — `btnInsert_Click` just opens the form. PK
 * `sales_allocation_scs_id` is auto-generated server-side.
 *
 * React shape mirrors the sibling `LotAllocationMassUploadDialog`: an editable
 * grid + paste TSV from Excel + a dry-run Cross-check + a single bulk POST.
 *
 * Grid columns = temp-table fields in Access INSERT order:
 *   Contract No*, DO No, M Date, M Truck No, From Warehouse, To Warehouse,
 *   M Net Weight, Container No, Seal No, No Bag, Tare Weight,
 *   Tare Carton Pallet, Tare Cont, Max Gross Weight, Allocating Bag,
 *   Allocating Quantity.
 *
 * Validation (Access `btnSave_Click` per-row scan, only when Contract No set):
 *   - Contract No must exist in mv_local_s          → "This contract is not existed !"
 *   - DO No must exist in v_delivery_order_scs       → "This DO is not existed !"
 *   - Seal No must not already be used (mv_sales_allocation_scs) → "This Seal No is existed !"
 * These run server-side on Cross-check / Upload (BE TODO); the FE surfaces the
 * verdicts per row. UI copy is English only and never reveals Access internals.
 *
 * ⚠️ Backend not built — every endpoint is a TODO contract (see
 * `saleAllocationMassUploadApi`); combos render empty until live, and
 * validate/upload surface errors via snackbar (no silent success, no fake data).
 */
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { useState } from 'react'

import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  type SaleAlcMassLookupItem,
  type SaleAlcMassRow,
  useBulkCreateSaleAlcMassMutation,
  useSaleAlcMassClientsQuery,
  useSaleAlcMassContractsQuery,
  useSaleAlcMassDeliveryOrdersQuery,
  useSaleAlcMassWarehousesQuery,
  useValidateSaleAlcMassMutation,
} from '../../store/api/saleAllocationMassUploadApi'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'

/** Editable grid row (all cells string while editing). */
interface MassRow {
  sale_contract: string
  delivery_order: string
  m_date: string // ISO YYYY-MM-DD ('' = blank)
  m_truck_no: string
  from_warehouse: string
  to_warehouse: string
  m_net_weight: string
  container_no: string
  seal_no: string
  number_bags: string
  tare_weight: string
  tare_carton_pallet: string
  tare_cont: string
  max_gross_weight: string
  allocating_bag: string
  allocating_quantity: string
}

type FieldKey = keyof MassRow
type FieldKind = 'text' | 'combo' | 'date' | 'number'

const FIELDS: Array<{ key: FieldKey; label: string; kind: FieldKind; required: boolean }> = [
  { key: 'sale_contract', label: 'Contract No', kind: 'combo', required: true },
  { key: 'delivery_order', label: 'DO No', kind: 'combo', required: false },
  { key: 'm_date', label: 'M Date', kind: 'date', required: false },
  { key: 'm_truck_no', label: 'M Truck No', kind: 'text', required: false },
  { key: 'from_warehouse', label: 'From Warehouse', kind: 'combo', required: false },
  { key: 'to_warehouse', label: 'To Warehouse', kind: 'combo', required: false },
  { key: 'm_net_weight', label: 'M Net Weight', kind: 'number', required: false },
  { key: 'container_no', label: 'Container No', kind: 'text', required: false },
  { key: 'seal_no', label: 'Seal No', kind: 'text', required: false },
  { key: 'number_bags', label: 'No Bag', kind: 'number', required: false },
  { key: 'tare_weight', label: 'Tare Weight', kind: 'number', required: false },
  { key: 'tare_carton_pallet', label: 'Tare Carton Pallet', kind: 'number', required: false },
  { key: 'tare_cont', label: 'Tare Cont', kind: 'number', required: false },
  { key: 'max_gross_weight', label: 'Max Gross Weight', kind: 'number', required: false },
  { key: 'allocating_bag', label: 'Allocating Bag', kind: 'number', required: false },
  { key: 'allocating_quantity', label: 'Allocating Quantity', kind: 'number', required: false },
]

const emptyRow = (): MassRow => ({
  sale_contract: '',
  delivery_order: '',
  m_date: '',
  m_truck_no: '',
  from_warehouse: '',
  to_warehouse: '',
  m_net_weight: '',
  container_no: '',
  seal_no: '',
  number_bags: '',
  tare_weight: '',
  tare_carton_pallet: '',
  tare_cont: '',
  max_gross_weight: '',
  allocating_bag: '',
  allocating_quantity: '',
})

/** Verdict for one row after cross-check (keyed by original grid index). */
interface RowVerdict {
  ok: boolean
  messages: string[]
  badFields: Set<FieldKey>
}

/** Strip thousand-sep + spaces → numeric string ('' if not parseable). */
function normNumber(raw: string): string {
  const cleaned = raw.replace(/,/g, '').replace(/\s+/g, '').trim()
  if (cleaned === '') return ''
  const n = Number(cleaned)
  return Number.isFinite(n) ? String(n) : ''
}

/** Access Format Standard `#,##0.00` — '' for null/blank. */
function formatStandard(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function toNum(v: string): number | null {
  const s = v.replace(/,/g, '').trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

/** Match `raw` against a combo option list (trim, case-insensitive) → canonical
 *  option string, '' if no match (drops pasted values outside the list). */
function canonicalOption(options: SaleAlcMassLookupItem[], raw: string): string {
  const v = (raw ?? '').replace(/\r$/, '').trim()
  if (!v) return ''
  const hit = options.find((o) => o.value.toLowerCase() === v.toLowerCase())
  return hit ? hit.value : ''
}

/** "a\tb\nc\td" → [['a','b'],['c','d']]. */
function parseTsv(text: string): string[][] {
  const trimmed = text.replace(/\r/g, '').replace(/\n$/, '')
  if (!trimmed) return []
  return trimmed.split('\n').map((line) => line.split('\t'))
}

interface Props {
  open: boolean
  canEdit: boolean
  onClose: () => void
  /** Fired after a successful upload (parent may refetch / close). */
  onUploaded?: () => void
}

export default function SalesAllocationMassUploadDialog({
  open,
  canEdit,
  onClose,
  onUploaded,
}: Props) {
  const snack = useSnackbar()
  const [client, setClient] = useState<string>('')
  const [rows, setRows] = useState<MassRow[]>([emptyRow()])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [verdictByRow, setVerdictByRow] = useState<Record<number, RowVerdict>>({})

  // Header combo `cp_vn` + grid combos. Skip until open; guard with `?? []`
  // (BE TODO → 404 → empty combo, no fake data).
  const clientsQuery = useSaleAlcMassClientsQuery(undefined, { skip: !open })
  const clientOptions = clientsQuery.data?.items ?? []
  const contractsQuery = useSaleAlcMassContractsQuery(
    client ? { client } : undefined,
    { skip: !open },
  )
  const contractOptions = contractsQuery.data?.items ?? []
  const doQuery = useSaleAlcMassDeliveryOrdersQuery(undefined, { skip: !open })
  const doOptions = doQuery.data?.items ?? []
  const warehousesQuery = useSaleAlcMassWarehousesQuery(undefined, { skip: !open })
  const warehouseOptions = warehousesQuery.data?.items ?? []

  const [validateMass, { isLoading: checking }] = useValidateSaleAlcMassMutation()
  const [bulkCreate, { isLoading: uploading }] = useBulkCreateSaleAlcMassMutation()

  // Editing any cell stales the verdicts → clear (all-or-nothing cross-check).
  const clearVerdicts = () => setVerdictByRow((v) => (Object.keys(v).length ? {} : v))
  const anyError = Object.values(verdictByRow).some((v) => !v.ok)

  const reset = () => {
    setRows([emptyRow()])
    setSelected(new Set())
    setVerdictByRow({})
  }

  const handleClose = () => {
    if (uploading) return
    onClose()
  }

  const setCell = (rowIdx: number, field: FieldKey, value: string) => {
    clearVerdicts()
    setRows((cur) => cur.map((r, i) => (i === rowIdx ? { ...r, [field]: value } : r)))
  }

  const optionsForKind = (key: FieldKey): SaleAlcMassLookupItem[] => {
    if (key === 'sale_contract') return contractOptions
    if (key === 'delivery_order') return doOptions
    if (key === 'from_warehouse' || key === 'to_warehouse') return warehouseOptions
    return []
  }

  // Paste TSV from (rowIdx, colIdx) — grow grid down/right. Combo cells keep only
  // values matching the lookup; number cells format Standard; date cells accept
  // an ISO/parseable date (else dropped).
  const handlePaste = (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    e.preventDefault()
    e.stopPropagation()
    clearVerdicts()
    const matrix = parseTsv(text)
    const coerce = (fld: (typeof FIELDS)[number], raw: string): string => {
      if (fld.kind === 'combo') return canonicalOption(optionsForKind(fld.key), raw)
      if (fld.kind === 'date') {
        const d = dayjs((raw ?? '').replace(/\r$/, '').trim())
        return d.isValid() ? d.format('YYYY-MM-DD') : ''
      }
      if (fld.kind === 'number') {
        const s = normNumber((raw ?? '').replace(/\r$/, ''))
        return s === '' ? '' : formatStandard(Number(s))
      }
      return (raw ?? '').replace(/\r$/, '').trim()
    }
    setRows((cur) => {
      const next = cur.map((r) => ({ ...r }))
      matrix.forEach((line, r) => {
        const targetRow = rowIdx + r
        while (next.length <= targetRow) next.push(emptyRow())
        line.forEach((cellRaw, c) => {
          const fld = FIELDS[colIdx + c]
          if (!fld) return
          next[targetRow][fld.key] = coerce(fld, cellRaw)
        })
      })
      return next
    })
  }

  const toggleRow = (idx: number) =>
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })

  const toggleAll = () =>
    setSelected((cur) => (cur.size === rows.length ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    if (selected.size === 0) return
    clearVerdicts()
    setRows((cur) => {
      const kept = cur.filter((_, i) => !selected.has(i))
      return kept.length ? kept : [emptyRow()]
    })
    setSelected(new Set())
  }

  // Filled rows (with original grid index) — skip fully-empty rows.
  const getFilled = () =>
    rows.map((r, i) => ({ r, i })).filter(({ r }) => FIELDS.some((f) => r[f.key].trim() !== ''))

  const buildPayload = (filled: { r: MassRow }[]): SaleAlcMassRow[] =>
    filled.map(({ r }) => ({
      sale_contract: r.sale_contract.trim(),
      delivery_order: r.delivery_order.trim(),
      m_date: r.m_date.trim() || null,
      m_truck_no: r.m_truck_no.trim(),
      from_warehouse: r.from_warehouse.trim(),
      to_warehouse: r.to_warehouse.trim(),
      m_net_weight: toNum(r.m_net_weight),
      container_no: r.container_no.trim(),
      seal_no: r.seal_no.trim(),
      number_bags: toNum(r.number_bags),
      tare_weight: toNum(r.tare_weight),
      tare_carton_pallet: toNum(r.tare_carton_pallet),
      tare_cont: toNum(r.tare_cont),
      max_gross_weight: toNum(r.max_gross_weight),
      allocating_bag: toNum(r.allocating_bag),
      allocating_quantity: toNum(r.allocating_quantity),
    }))

  // Local required gate (Access scan keys off Contract No being set).
  const localBlocking = (): number[] =>
    getFilled()
      .filter(({ r }) => r.sale_contract.trim() === '')
      .map(({ i }) => i)

  // Cross-check dry-run — BE validates each row (contract/DO existence, seal
  // uniqueness) → map verdict by original index. Returns true if all rows OK.
  const runValidate = async (opts?: { quiet?: boolean }): Promise<boolean> => {
    const filled = getFilled()
    if (filled.length === 0) {
      snack.warning('At least 1 row is required')
      return false
    }
    // Local required check first (Contract No) so the user sees it without BE.
    const missing = localBlocking()
    if (missing.length > 0) {
      const map: Record<number, RowVerdict> = {}
      missing.forEach((idx) => {
        map[idx] = {
          ok: false,
          messages: ['Contract No is required'],
          badFields: new Set<FieldKey>(['sale_contract']),
        }
      })
      setVerdictByRow(map)
      snack.error(`${missing.length} row(s) missing Contract No`)
      return false
    }
    try {
      const res = await validateMass({
        client: client || undefined,
        rows: buildPayload(filled),
      }).unwrap()
      const map: Record<number, RowVerdict> = {}
      res.rows.forEach((v, k) => {
        const origIdx = filled[k]?.i
        if (origIdx == null) return
        map[origIdx] = {
          ok: v.ok,
          messages: v.errors.map((e) => e.message),
          badFields: new Set(
            v.errors
              .map((e) => e.field)
              .filter((f): f is FieldKey => !!f),
          ),
        }
      })
      setVerdictByRow(map)
      const badCount = res.rows.filter((v) => !v.ok).length
      if (badCount > 0) snack.error(`Cross-check: ${badCount} row(s) failed — fix highlighted rows`)
      else if (!opts?.quiet) snack.success(`Cross-check passed (${res.rows.length} rows)`)
      return badCount === 0
    } catch (err) {
      snack.error(getErrorMessage(err) || 'Could not run cross-check')
      return false
    }
  }

  // Upload = cross-check first (all-or-nothing), then a single bulk insert.
  const runUpload = async () => {
    if (!canEdit) return
    const ok = await runValidate({ quiet: true })
    if (!ok) return
    const filled = getFilled()
    try {
      const res = await bulkCreate({
        client: client || undefined,
        rows: buildPayload(filled),
      }).unwrap()
      snack.success(`Uploaded ${res.created}/${filled.length} rows`)
      reset()
      onUploaded?.()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err) || 'Could not upload rows')
      void runValidate({ quiet: true })
    }
  }

  const allSelected = rows.length > 0 && selected.size === rows.length

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (uploading || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { maxHeight: 'min(760px, 94vh)' } }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          noWrap
          title="Mass Upload — Sales Allocation"
          sx={{ ...TEXT_TRUNCATE, flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {'Mass Upload — '}
          </Box>
          Sales Allocation
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Paste rows from Excel, or fill the grid below. Contract No is required on every row;
          Contract No, DO No and warehouses must match an existing value (anything else is
          dropped). Empty rows are skipped on upload.
        </Typography>

        {/* Header Client combo (Access `cp_vn`). */}
        <Box sx={{ maxWidth: 360, mb: 1.5 }}>
          <Autocomplete
            size="small"
            options={clientOptions}
            value={clientOptions.find((o) => o.value === client) ?? (client ? { value: client, label: client } : null)}
            onChange={(_e, v) => setClient(v?.value ?? '')}
            getOptionLabel={(o) => (o ? (o.value === o.label ? o.value : `${o.value} — ${o.label}`) : '')}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            loading={clientsQuery.isFetching}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              clearVerdicts()
              setRows((c) => [...c, emptyRow()])
            }}
          >
            Add row
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={selected.size === 0}
            onClick={deleteSelected}
          >
            Delete selected
          </Button>
          <Button size="small" variant="outlined" color="inherit" onClick={reset}>
            Reset
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `40px 40px 36px repeat(${FIELDS.length}, minmax(130px, 1fr))`,
            minWidth: 'max-content',
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            overflow: 'auto',
            '& > .hdr': {
              bgcolor: 'action.hover',
              fontWeight: 700,
              fontSize: '0.72rem',
              px: 1,
              py: 0.75,
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              whiteSpace: 'nowrap',
            },
          }}
        >
          {/* Header row */}
          <Box
            className="hdr"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Checkbox size="small" checked={allSelected} onChange={toggleAll} sx={{ p: 0 }} />
          </Box>
          <Box className="hdr" sx={{ textAlign: 'center' }}>
            #
          </Box>
          <Box
            className="hdr"
            sx={{ textAlign: 'center' }}
            title="Cross-check status"
            aria-label="Cross-check status"
          />
          {FIELDS.map((f) => (
            <Box key={f.key} className="hdr">
              {f.label}
              {f.required ? <span style={{ color: '#d32f2f' }}> *</span> : null}
            </Box>
          ))}

          {/* Data rows */}
          {rows.map((row, rowIdx) => (
            <RowCells
              key={rowIdx}
              row={row}
              rowIdx={rowIdx}
              contractOptions={contractOptions}
              doOptions={doOptions}
              warehouseOptions={warehouseOptions}
              selected={selected.has(rowIdx)}
              verdict={verdictByRow[rowIdx]}
              onToggle={() => toggleRow(rowIdx)}
              onCell={(field, value) => setCell(rowIdx, field, value)}
              onPaste={handlePaste}
            />
          ))}
        </Box>

        {/* Cross-check error summary — visible, no hover needed. */}
        {anyError ? (
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.error.light}`,
              bgcolor: 'rgba(211,47,47,0.06)',
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: 'error.main', display: 'block', mb: 0.5 }}
            >
              Cross-check issues — fix before upload
            </Typography>
            <Stack spacing={0.25}>
              {Object.entries(verdictByRow)
                .filter(([, v]) => !v.ok)
                .sort((a, b) => Number(a[0]) - Number(b[0]))
                .map(([idx, v]) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    sx={{ display: 'block', color: 'error.main', fontWeight: 600 }}
                  >
                    {`Row ${Number(idx) + 1}: ${v.messages.join('; ')}`}
                  </Typography>
                ))}
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Typography
          variant="caption"
          sx={{
            mr: 'auto',
            color: anyError ? 'error.main' : 'text.secondary',
            fontWeight: anyError ? 700 : 400,
          }}
        >
          {anyError
            ? `${Object.values(verdictByRow).filter((v) => !v.ok).length} row(s) failed cross-check — fix to enable Upload`
            : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
        </Typography>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={uploading || checking}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => void runValidate()}
          disabled={uploading || checking}
        >
          {checking ? <CircularProgress size={16} color="inherit" /> : 'Cross-check'}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={() => void runUpload()}
          disabled={!canEdit || uploading || checking || anyError}
        >
          {uploading ? <CircularProgress size={16} color="inherit" /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const cellSx = {
  '& .MuiInputBase-root': { borderRadius: 0, fontSize: '0.78rem' },
  '& .MuiInputBase-input': { py: 0.5, px: 1 },
  '& fieldset': { border: 'none' },
}

const comboSx = {
  ...cellSx,
  '& .MuiAutocomplete-endAdornment': { right: 2 },
  '& .MuiInputBase-root': { ...cellSx['& .MuiInputBase-root'], pr: '36px !important' },
}

/** Restricted combo cell — value always matches options (no freeSolo). */
function ComboCell({
  value,
  options,
  loading,
  onChange,
  onPaste,
}: {
  value: string
  options: SaleAlcMassLookupItem[]
  loading?: boolean
  onChange: (value: string) => void
  onPaste: (e: React.ClipboardEvent<HTMLElement>) => void
}) {
  const matched = options.find((o) => o.value === value) ?? null
  return (
    <Autocomplete
      options={options}
      value={matched}
      onChange={(_, v) => onChange(v?.value ?? '')}
      getOptionLabel={(o) => (o ? (o.value === o.label ? o.value : `${o.value} — ${o.label}`) : '')}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      size="small"
      fullWidth
      loading={loading}
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      sx={comboSx}
      renderInput={(params) => (
        <TextField
          {...params}
          onPasteCapture={onPaste}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

function RowCells({
  row,
  rowIdx,
  contractOptions,
  doOptions,
  warehouseOptions,
  selected,
  verdict,
  onToggle,
  onCell,
  onPaste,
}: {
  row: MassRow
  rowIdx: number
  contractOptions: SaleAlcMassLookupItem[]
  doOptions: SaleAlcMassLookupItem[]
  warehouseOptions: SaleAlcMassLookupItem[]
  selected: boolean
  verdict?: RowVerdict
  onToggle: () => void
  onCell: (field: FieldKey, value: string) => void
  onPaste: (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => void
}) {
  const failed = !!verdict && !verdict.ok
  const cellBg = (field?: FieldKey): string | undefined =>
    failed
      ? field && verdict!.badFields.has(field)
        ? 'rgba(211,47,47,0.28)'
        : 'rgba(211,47,47,0.07)'
      : undefined

  const optionsForKind = (key: FieldKey): SaleAlcMassLookupItem[] => {
    if (key === 'sale_contract') return contractOptions
    if (key === 'delivery_order') return doOptions
    if (key === 'from_warehouse' || key === 'to_warehouse') return warehouseOptions
    return []
  }

  const cellBorder = (field?: FieldKey) => ({
    borderBottom: (t: { palette: { divider: string } }) => `1px solid ${t.palette.divider}`,
    minWidth: 0,
    bgcolor: cellBg(field),
  })

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: selected ? 'action.selected' : cellBg(),
        }}
      >
        <Checkbox size="small" checked={selected} onChange={onToggle} sx={{ p: 0 }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.72rem',
          color: 'text.secondary',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: cellBg(),
        }}
      >
        {rowIdx + 1}
      </Box>
      {/* Cross-check status — ✓ pass / ✗ fail (tooltip lists reasons). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: cellBg(),
        }}
      >
        {verdict ? (
          verdict.ok ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'success.main' }} />
          ) : (
            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{verdict.messages.join('\n')}</Box>}>
              <ErrorRoundedIcon sx={{ fontSize: 16, color: 'error.main' }} />
            </Tooltip>
          )
        ) : null}
      </Box>

      {FIELDS.map((f, colIdx) => {
        if (f.kind === 'combo') {
          return (
            <Box key={f.key} sx={cellBorder(f.key)}>
              <ComboCell
                value={row[f.key]}
                options={optionsForKind(f.key)}
                onChange={(v) => onCell(f.key, v)}
                onPaste={(e) => onPaste(rowIdx, colIdx, e)}
              />
            </Box>
          )
        }
        if (f.kind === 'date') {
          return (
            <Box key={f.key} sx={cellBorder(f.key)}>
              <DatePicker
                format="DD-MMM-YYYY"
                value={row[f.key] ? dayjs(row[f.key]) : null}
                onChange={(d) =>
                  onCell(f.key, d && d.isValid() ? d.format('YYYY-MM-DD') : '')
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: '—',
                    onPasteCapture: (e: React.ClipboardEvent<HTMLElement>) =>
                      onPaste(rowIdx, colIdx, e),
                    sx: cellSx,
                  },
                }}
              />
            </Box>
          )
        }
        if (f.kind === 'number') {
          return (
            <TextField
              key={f.key}
              value={row[f.key]}
              onChange={(e) => onCell(f.key, e.target.value.replace(/[^\d.,]/g, ''))}
              onBlur={() => {
                const n = toNum(row[f.key])
                onCell(f.key, n == null ? '' : formatStandard(n))
              }}
              onPasteCapture={(e) => onPaste(rowIdx, colIdx, e)}
              size="small"
              fullWidth
              variant="outlined"
              placeholder="—"
              inputProps={{ style: { textAlign: 'right' }, inputMode: 'decimal' }}
              sx={{
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: cellBg(f.key),
                ...cellSx,
              }}
            />
          )
        }
        // text
        return (
          <TextField
            key={f.key}
            value={row[f.key]}
            onChange={(e) => onCell(f.key, e.target.value)}
            onPasteCapture={(e) => onPaste(rowIdx, colIdx, e)}
            size="small"
            fullWidth
            variant="outlined"
            placeholder="—"
            sx={{
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: cellBg(f.key),
              ...cellSx,
            }}
          />
        )
      })}
    </>
  )
}
